import { Order } from "../Models/Order";
import { RawOrder } from "../Models/RawOrder";
import { IAuthService } from "./Auth/AuthService";
import { Cache } from "./Cache";
import { OrderConverter } from "./Converters/Order/OrderConverter";
import { DateTimeProvider } from "./DateTimeProvider";
import { INotifier } from "./Notifier/INotifier";
import { IRepo } from "./Storage/IRepo";
import { Throttle } from "../Utils/Throttle";
import { Ticket } from "../Models/Ticket";
import { TicketToOrderConverter } from "./TicketToOrderConverter";
import { BitcoinPriceProvider } from "../Steps/Payment/Services/BitcoinPriceProvider";
import { PaymentForm } from "../Models/Payment/PaymentForm";
import { v4 as uuidv4 } from 'uuid';

export class OrdersManager
{
    private cache = new Cache<RawOrder>();

    private Log(message: string)
    {
        // console.log(message);
    }

    constructor(
        private _ordersRepo: IRepo<RawOrder>,
        private _ticketsRepo: IRepo<Ticket>,
        private _ticketConverter: TicketToOrderConverter,
        private _orderConverter: OrderConverter,
        private _auth: IAuthService,
        private _notifier: INotifier,
        private _bitcoinPriceProvider: BitcoinPriceProvider
    )
    { }

    public async OpenTicket(ticketId: string): Promise<Order | null>
    {
        const ticket = await this._ticketsRepo.GetOne("Open", ticketId);

        if (!ticket)
        {
            return null;
        }

        if (ticket.BasketPaymentForm == PaymentForm.Crypto)
        {
            await this._bitcoinPriceProvider.Update();
        }

        const btcValueInPln = this._bitcoinPriceProvider.BtcValueInPln.value ?? (-1);

        const order = this._ticketConverter.Convert(ticket, btcValueInPln);

        order.SelfSetup();

        await this.Add(order);

        await this._ticketsRepo.Move("Open", "Closed", ticketId);

        return order;
    }

    public async Add(order: Order): Promise<void>
    {
        const raw = this._orderConverter.ToRaw(order);
        if (!raw)
        {
            throw new Error("Failed to convert order to raw");
        }

        await this._ordersRepo.Update(raw.Meta.Folder, raw.Id, raw);

        this.cache.UpdateOrAdd(raw);
    }

    public async GetOne(orderId: string): Promise<Order | null>
    {
        // 1. Check if order is in cache
        let orderFromCache: RawOrder | null = this.cache.FindOne(orderId);

        if (orderFromCache)
        {
            this.Log('Found in cache');

            const order = this._orderConverter.FromRaw(orderFromCache);

            if (order)
            {
                return order;
            }
        }

        // 2. Check if order is in repo
        const orderFromRepo: RawOrder | null = await this._ordersRepo.FindOne(orderId);

        if (orderFromRepo)
        {
            this.Log('Found in repo');

            this.cache.UpdateOrAdd(orderFromRepo);

            const order = this._orderConverter.FromRaw(orderFromRepo);

            if (order)
            {
                return order;
            }
        }

        // 3. Check if order is in tickets
        const orderFromTicket: Order | null = await this.OpenTicket(orderId);

        if (orderFromTicket)
        {
            this.Log('Found in ticket');

            const rawOrderFromTicket = this._orderConverter.ToRaw(orderFromTicket);

            if (rawOrderFromTicket)
            {
                this.cache.UpdateOrAdd(rawOrderFromTicket);

                if (orderFromTicket)
                {
                    return orderFromTicket;
                }
            }
        }

        return null;
    }

    public async GetOneRaw(id: string): Promise<RawOrder | null>
    {
        let rawOrder: RawOrder | null = this.cache.FindOne(id);
        if (!rawOrder)
        {
            rawOrder = await this._ordersRepo.FindOne(id);
            if (!rawOrder)
            {
                return null;
            }

            this.cache.UpdateOrAdd(rawOrder);
        }

        return rawOrder;
    }

    public async GetAll(folder: string = "Current"): Promise<Order[]>
    {
        // 1. Get all orders from repo
        const rawOrders = await this._ordersRepo.GetAll(folder);

        this.cache.UpdateOrAdd(...rawOrders);

        const orders = rawOrders.map(x => this._orderConverter.FromRaw(x)).filter(x => x != null)

        // 2. Get all orders from tickets only if requesting current orders
        if (folder === "Current")
        {
            const ticketsIds = await this._ticketsRepo.GetAllIds("Open");

            for (const ticketId of ticketsIds)
            {
                const orderFromTicket = await this.OpenTicket(ticketId);

                if (orderFromTicket)
                {
                    const rawOrderFromTicket = this._orderConverter.ToRaw(orderFromTicket);

                    if (rawOrderFromTicket)
                    {
                        this.cache.UpdateOrAdd(rawOrderFromTicket);

                        orders.push(orderFromTicket);
                    }
                }
            }
        }

        return orders;
    }

    public async Update(order: Order, updateInFolder = order.Meta.Folder): Promise<void>
    {
        const canNotify = Throttle.Throttle(order.Meta.UpdatedAt, 10 * 60);

        order.Meta.UpdatedAt = DateTimeProvider.Now;
        order.Meta.UpdatedBy = this._auth.UserInfo?.User.Id || "(unknown user id)";

        const raw = this._orderConverter.ToRaw(order);

        if (!raw)
        {
            throw new Error("Failed to convert order to raw");
        }

        await this._ordersRepo.Update(updateInFolder, raw.Id, raw);

        this.cache.UpdateOrAdd(raw);

        if (canNotify)
        {
            await this._notifier.NotifyOrderUpdate(order);
        }
    }

    public async UpdateRaw(raw: RawOrder): Promise<void>
    {
        raw.Meta.UpdatedAt = DateTimeProvider.Now;
        raw.Meta.UpdatedBy = this._auth.UserInfo?.User.Id || "(unknown user id)";

        await this._ordersRepo.Update(raw.Meta.Folder, raw.Id, raw);

        this.cache.UpdateOrAdd(raw);
    }

    private async Move(order: Order, targetFolder: string): Promise<void>
    {
        const sourceFolder = order.Meta.Folder;
        order.Meta.Folder = targetFolder;

        await this.Update(order, sourceFolder);

        await this._ordersRepo.Move(sourceFolder, targetFolder, order.Id);
    }

    public async Close(order: Order): Promise<void>
    {
        await this.Move(order, "Closed");
    }

    public async Reopen(order: Order): Promise<void>
    {
        await this.Move(order, "Current");
    }

    public async Delete(order: Order): Promise<void>
    {
        await this._ordersRepo.Delete(order.Meta.Folder, order.Id);

        let rawOrder: RawOrder | null = this.cache.FindOne(order.Id);
        if (rawOrder)
            this.cache.Remove(rawOrder);
    }

    public async Drop(): Promise<void>
    {
        await this._ordersRepo.Drop();
        await this._ticketsRepo.Drop();
        this.cache.Clear();
    }

    public async Duplicate(order: Order): Promise<Order>
    {
        const newOrder = new Order();

        newOrder.Id = uuidv4().toUpperCase();

        newOrder.Meta.CreatedAt = DateTimeProvider.Now;
        newOrder.Meta.CreatedBy = this._auth.UserInfo?.User.Id || "(unknown user id)";

        const steps = [...order.Steps.Items];
        steps.forEach(x => x.ResetState());
        newOrder.Steps.Load(...steps);

        newOrder.People.Load(...order.People.Items);

        await this.Add(newOrder);

        return newOrder;
    }
}
